import React, { useContext } from "react"

import { WithID } from "shared/types/WithID"

import { TableContext } from "../TableContext"
import { DragMoveContext, TableDnDContext } from "../dn-d/TableDnDContext"
import { RowRendererProps, TableItemProps } from "../types"
import { useRowViewModel } from "./hooks/useRowViewModel"

export interface RowInnerComponentProps<I extends WithID>
  extends TableItemProps<I> {
  rowHeight: number
  isCollapsed?: boolean
  isPrevParent: boolean
  isNextParent: boolean
}

export interface RowViewModel<I extends WithID> extends RowRendererProps<I> {
  RowRenderer: React.ComponentType<RowRendererProps<I>> | undefined
}

function RowInnerComponent<I extends WithID>(props: RowInnerComponentProps<I>) {
  const { RowRenderer, ...viewModel } = useRowViewModel(props)

  if (!RowRenderer) {
    return null
  }

  return <RowRenderer {...viewModel} />
}

const RowInner = React.memo(RowInnerComponent) as typeof RowInnerComponent

export function RowComponent<I extends WithID>(props: TableItemProps<I>) {
  const { item } = props

  const { dragSource } = useContext(TableDnDContext)
  const { nextParent } = useContext(DragMoveContext)
  const { rowHeight } = useContext(TableContext)

  const isPrevParent = dragSource?.meta.parentID === item.id
  const isNextParent = nextParent === item.id

  return (
    <RowInner
      {...props}
      rowHeight={rowHeight || 0}
      isPrevParent={isPrevParent}
      isNextParent={isNextParent}
    />
  )
}

export const Row = React.memo(RowComponent) as typeof RowComponent
